import { combineReducers } from "redux";
import { ActionSchema } from "../actions";
import {
  IntegrationsConst,
  GetDataExportDumpRequestsDataSchema,
  RemoveDataExportDumpRequestDataSchema,
  GetExportLogsSchema,
  GetExportLogDownloadUrlSchema,
  GetWebHookRequestsSchema,
  DeleteWebHookResponseSchema,
  AddWebHookResponseSchema,
  AvailableIntegrationsSchema,
  IntegrationSubscriptionsSchema,
  ActivateIntegrationSchema,
  DeactivateIntegrationSchema,
  GetIntegrationConfigSchema,
  SaveIntegrationDataSchema,
  UpdateWebHookResponseSchema,
  GetAuditLogConfigSchema,
  SaveAuditLogConfigSchema,
  ResetAuditLogConfigSchema,
  AuditLogLimitsSchema,
  // UPDATE_AUDIT_LOG_DATA handled in saveAuditLogConfigReducer
  GetWebhookEventsSchema
} from "../actions/integrationsActions";
import { IntegrationListSlice, IntegrationListState } from "../features/integration/cloudConnectors";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../services/analytics/tracking";

const withRequestTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...getRequestTrackingState(action as any)
});

const withSuccessTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "success")
});

const withErrorTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "error")
});

const getDataExportDumpRequestsReducer = (state = {}, action: ActionSchema<GetDataExportDumpRequestsDataSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_DATA_EXPORT_DUMP_REQUESTS_LOADING:
    case IntegrationsConst.GET_DATA_EXPORT_DUMP_REQUESTS_SUCCESS:
    case IntegrationsConst.GET_DATA_EXPORT_DUMP_REQUESTS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const removeDataExportDumpRequestReducer = (state = {}, action: ActionSchema<RemoveDataExportDumpRequestDataSchema>) => {
  switch (action.type) {
    case IntegrationsConst.REMOVE_DATA_EXPORT_DUMP_REQUEST:
      return withRequestTracking(state, action);
    case IntegrationsConst.REMOVE_DATA_EXPORT_DUMP_REQUEST_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.REMOVE_DATA_EXPORT_DUMP_REQUEST_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.REMOVE_DATA_EXPORT_DUMP_REQUEST_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const addDataExportDumpRequestReducer = (state = {}, action: ActionSchema<GetDataExportDumpRequestsDataSchema>) => {
  switch (action.type) {
    case IntegrationsConst.ADD_DATA_EXPORT_DUMP_REQUEST:
      return withRequestTracking(state, action);
    case IntegrationsConst.ADD_DATA_EXPORT_DUMP_REQUEST_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.ADD_DATA_EXPORT_DUMP_REQUEST_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.ADD_DATA_EXPORT_DUMP_REQUEST_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getExportLogsReducer = (state = {}, action: ActionSchema<GetExportLogsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_EXPORT_LOGS_LOADING:
    case IntegrationsConst.GET_EXPORT_LOGS_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_EXPORT_LOGS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getExportLogDownloadUrlReducer = (state = {}, action: ActionSchema<GetExportLogDownloadUrlSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_EXPORT_LOG_DOWNLOAD_URL_LOADING:
    case IntegrationsConst.GET_EXPORT_LOG_DOWNLOAD_URL_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_EXPORT_LOG_DOWNLOAD_URL_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getAuditLogConfigReducer = (state = {}, action: ActionSchema<GetAuditLogConfigSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_AUDIT_LOG_DATA_LOADING:
    case IntegrationsConst.GET_AUDIT_LOG_DATA_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_AUDIT_LOG_DATA_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const saveAuditLogConfigReducer = (state = {}, action: ActionSchema<SaveAuditLogConfigSchema>) => {
  switch (action.type) {
    case IntegrationsConst.SAVE_AUDIT_LOG_DATA:
    case IntegrationsConst.UPDATE_AUDIT_LOG_DATA:
      return withRequestTracking(state, action);
    case IntegrationsConst.SAVE_AUDIT_LOG_DATA_LOADING:
    case IntegrationsConst.UPDATE_AUDIT_LOG_DATA_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.SAVE_AUDIT_LOG_DATA_SUCCESS:
    case IntegrationsConst.UPDATE_AUDIT_LOG_DATA_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.SAVE_AUDIT_LOG_DATA_ERROR:
    case IntegrationsConst.UPDATE_AUDIT_LOG_DATA_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const resetAuditLogConfigReducer = (state = {}, action: ActionSchema<ResetAuditLogConfigSchema>) => {
  switch (action.type) {
    case IntegrationsConst.DELETE_AUDIT_LOG_DATA:
      return withRequestTracking(state, action);
    case IntegrationsConst.DELETE_AUDIT_LOG_DATA_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.DELETE_AUDIT_LOG_DATA_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.DELETE_AUDIT_LOG_DATA_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getAuditLogLimitsReducer = (state = {}, action: ActionSchema<AuditLogLimitsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_AUDIT_LOG_LIMITS_LOADING:
    case IntegrationsConst.GET_AUDIT_LOG_LIMITS_SUCCESS:
    case IntegrationsConst.GET_AUDIT_LOG_LIMITS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getWebHookRequestsReducer = (state = {}, action: ActionSchema<GetWebHookRequestsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_WEB_HOOK_REQUESTS_LOADING:
    case IntegrationsConst.GET_WEB_HOOK_REQUESTS_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_WEB_HOOK_REQUESTS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getWebhookEventsReducer = (state = {}, action: ActionSchema<GetWebhookEventsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_WEBHOOK_EVENTS_LOADING:
    case IntegrationsConst.GET_WEBHOOK_EVENTS_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_WEBHOOK_EVENTS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteWebHookReducer = (state = {}, action: ActionSchema<DeleteWebHookResponseSchema>) => {
  switch (action.type) {
    case IntegrationsConst.DELETE_WEB_HOOK:
      return withRequestTracking(state, action);
    case IntegrationsConst.DELETE_WEB_HOOK_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.DELETE_WEB_HOOK_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.DELETE_WEB_HOOK_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const updateWebHookReducer = (state = {}, action: ActionSchema<UpdateWebHookResponseSchema>) => {
  switch (action.type) {
    case IntegrationsConst.UPDATE_WEB_HOOK:
      return withRequestTracking(state, action);
    case IntegrationsConst.UPDATE_WEB_HOOK_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.UPDATE_WEB_HOOK_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.UPDATE_WEB_HOOK_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const addWebHookReducer = (state = {}, action: ActionSchema<AddWebHookResponseSchema>) => {
  switch (action.type) {
    case IntegrationsConst.ADD_WEB_HOOK:
      return withRequestTracking(state, action);
    case IntegrationsConst.ADD_WEB_HOOK_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.ADD_WEB_HOOK_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.ADD_WEB_HOOK_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getAvailableIntegrationsReducer = (state = {}, action: ActionSchema<AvailableIntegrationsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_AVAILABLE_INTEGRATIONS_LOADING:
    case IntegrationsConst.GET_AVAILABLE_INTEGRATIONS_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_AVAILABLE_INTEGRATIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getIntegrationSubscriptionsReducer = (state = {}, action: ActionSchema<IntegrationSubscriptionsSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_INTEGRATION_SUBSCRIPTIONS_LOADING:
    case IntegrationsConst.GET_INTEGRATION_SUBSCRIPTIONS_SUCCESS:
    case IntegrationsConst.GET_INTEGRATION_SUBSCRIPTIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const activateIntegrationReducer = (state = {}, action: ActionSchema<ActivateIntegrationSchema>) => {
  switch (action.type) {
    case IntegrationsConst.ACTIVATE_INTEGRATION:
      return withRequestTracking(state, action);
    case IntegrationsConst.ACTIVATE_INTEGRATION_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.ACTIVATE_INTEGRATION_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.ACTIVATE_INTEGRATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const deactivateIntegrationReducer = (state = {}, action: ActionSchema<DeactivateIntegrationSchema>) => {
  switch (action.type) {
    case IntegrationsConst.DEACTIVATE_INTEGRATION:
      return withRequestTracking(state, action);
    case IntegrationsConst.DEACTIVATE_INTEGRATION_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.DEACTIVATE_INTEGRATION_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.DEACTIVATE_INTEGRATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getIntegrationConfigReducer = (state = {}, action: ActionSchema<GetIntegrationConfigSchema>) => {
  switch (action.type) {
    case IntegrationsConst.GET_INTEGRATION_CONFIG_LOADING:
    case IntegrationsConst.GET_INTEGRATION_CONFIG_SUCCESS:
      return { ...state, ...action.payload };
    case IntegrationsConst.GET_INTEGRATION_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const saveIntegrationDataReducer = (state = {}, action: ActionSchema<SaveIntegrationDataSchema>) => {
  switch (action.type) {
    case IntegrationsConst.SAVE_INTEGRATION_DATA:
      return withRequestTracking(state, action);
    case IntegrationsConst.SAVE_INTEGRATION_DATA_LOADING:
      return { ...state, ...action.payload };
    case IntegrationsConst.SAVE_INTEGRATION_DATA_SUCCESS:
      return withSuccessTracking(state, action);
    case IntegrationsConst.SAVE_INTEGRATION_DATA_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

// Combine Reducers
const integrationsReducer = combineReducers({
  dataExportDumpRequests: getDataExportDumpRequestsReducer,
  removeDataExportDumpRequestStatus: removeDataExportDumpRequestReducer,
  addDataExportDumpRequestStatus: addDataExportDumpRequestReducer,
  exportLogs: getExportLogsReducer,
  exportLogDownloadUrl: getExportLogDownloadUrlReducer,
  webHookRequests: getWebHookRequestsReducer,
  deleteWebHookResponse: deleteWebHookReducer,
  addWebHookResponse: addWebHookReducer,
  updateWebHookResponse: updateWebHookReducer,
  availableIntegrations: getAvailableIntegrationsReducer,
  integrationList: IntegrationListSlice.reducer,
  integrationSubscriptions: getIntegrationSubscriptionsReducer,
  activateIntegration: activateIntegrationReducer,
  deactivateIntegration: deactivateIntegrationReducer,
  integrationConfig: getIntegrationConfigReducer,
  saveIntegrationData: saveIntegrationDataReducer,
  AuditLogConfig: getAuditLogConfigReducer,
  auditLogLimits: getAuditLogLimitsReducer,
  saveAuditLogConfigResponse: saveAuditLogConfigReducer,
  resetAuditLogConfigResponse: resetAuditLogConfigReducer,
  getWebhookEvents: getWebhookEventsReducer
});

// Combine schemas
export interface IntegrationsSchema {
  dataExportDumpRequests: GetDataExportDumpRequestsDataSchema;
  removeDataExportDumpRequestStatus: RemoveDataExportDumpRequestDataSchema;
  addDataExportDumpRequestStatus: GetDataExportDumpRequestsDataSchema;
  exportLogs: GetExportLogsSchema;
  exportLogDownloadUrl: GetExportLogDownloadUrlSchema;
  webHookRequests: GetWebHookRequestsSchema;
  deleteWebHookResponse: DeleteWebHookResponseSchema;
  addWebHookResponse: AddWebHookResponseSchema;
  updateWebHookResponse: UpdateWebHookResponseSchema;
  availableIntegrations: AvailableIntegrationsSchema;
  integrationList: IntegrationListState;
  integrationSubscriptions: IntegrationSubscriptionsSchema;
  activateIntegration: ActivateIntegrationSchema;
  deactivateIntegration: DeactivateIntegrationSchema;
  integrationConfig: GetIntegrationConfigSchema;
  saveIntegrationData: SaveIntegrationDataSchema;
  AuditLogConfig: GetAuditLogConfigSchema;
  auditLogLimits: AuditLogLimitsSchema;
  saveAuditLogConfigResponse: SaveAuditLogConfigSchema;
  resetAuditLogConfigResponse: ResetAuditLogConfigSchema;
  getWebhookEvents: GetWebhookEventsSchema;
}

export default integrationsReducer;
